import type { CityConfig } from "@/pages/city-template/types";

export const pahrumpConfig: CityConfig = {
  city: "Pahrump",
  state: "Nevada",
  stateAbbr: "NV",
  slug: "pahrump",
  route: "/cities/pahrump",
  title: "Emergency Passport Pahrump NV | Expedited Passport Nye County Nevada",
  metaDescription: "Emergency passport and expedited passport services in Pahrump, NV — as fast as 24 hours. Fast Passport Center serves Pahrump, Nye County, Beatty, Amargosa Valley, and the Death Valley region. Registered State Dept. courier. A+ BBB rated. No office visit required.",
  h1: "Emergency & Expedited Passport Services in Pahrump, Nevada",
  heroSubtitle: "Last-minute trip to Mexico booked and your passport expired? Death Valley international visitors arriving and you need to meet them abroad? Vegas gateway trip to Asia or Europe coming up fast? Fast Passport Center helps Pahrump and Nye County residents get expedited passports as fast as 24 hours — entirely remotely. The Los Angeles Passport Agency is 280 miles away — we serve you without the desert drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Pahrump%20Nevada%20desert%20landscape%20with%20the%20Spring%20Mountains%20in%20the%20background%20under%20dramatic%20high%20desert%20sunset%20sky%2C%20wide%20open%20Nye%20County%20valley%20with%20sagebrush%20and%20Joshua%20trees%2C%20the%20town%20of%20Pahrump%20nestled%20between%20the%20Nevada-California%20border%20region%2C%20panoramic%20view%20of%20the%20Mojave%20Desert%20gateway%20community%20with%20warm%20golden%20hour%20light%20and%20rugged%20mountain%20silhouettes%2C%20no%20people%20or%20text%20visible&width=1800&height=700&seq=pahrump-nv-hero-01&orientation=landscape",
  heroImageAlt: "Pahrump Nevada desert valley with Spring Mountains at sunset in Nye County",
  geoPosition: "36.2083;-115.9906",
  geoPlacename: "Pahrump, Nevada",
  lastModified: "2026-06-04",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "11000 Wilshire Blvd, Suite 1000, Los Angeles, CA 90024",
    distance: "About 280 miles southwest — approximately 4.5 hours by car via NV-160 and I-15.",
  },
  nearbyCitiesZips: [
    { name: "Pahrump", zips: "89048, 89060, 89061" },
    { name: "Beatty", zips: "89003" },
    { name: "Amargosa Valley", zips: "89020" },
    { name: "Crystal", zips: "89048" },
    { name: "Shoshone", zips: "92384" },
    { name: "Tecopa", zips: "92389" },
    { name: "Sandy Valley", zips: "89019" },
    { name: "Mountain Springs", zips: "89161" },
    { name: "Death Valley Junction", zips: "92328" },
    { name: "Indian Springs", zips: "89018" },
    { name: "Blue Diamond", zips: "89004" },
    { name: "Las Vegas (southwest)", zips: "89113, 89118, 89139" },
  ],
  benefits: [
    { icon: "ri-computer-line", title: "Start 100% Online from Pahrump", desc: "Complete your emergency or expedited passport order from anywhere in Nye County — no desert drive to Los Angeles or even Las Vegas required." },
    { icon: "ri-truck-line", title: "Overnight Shipping from Pahrump or Las Vegas", desc: "Ship from The UPS Store in Pahrump or any Las Vegas FedEx Office — both support overnight delivery for expedited submissions." },
    { icon: "ri-shield-check-line", title: "Expert Document Review for Desert Residents", desc: "Every application reviewed before submission — essential for Pahrump residents in one of the largest unincorporated communities in the U.S." },
    { icon: "ri-calendar-check-line", title: "Reserved Agency Submission Access", desc: "We bypass the public appointment backlog with reserved Los Angeles Passport Agency slots — far faster than driving 4.5 hours through the Mojave Desert." },
  ],
  testimonials: [
    {
      name: "Barbara L.",
      location: "Pahrump, NV",
      rating: 5,
      test: "We booked an all-inclusive resort in Puerto Vallarta for our anniversary and two weeks before departure I realized my passport was expired. We're an hour from Vegas and 4.5 from LA — not great options. Fast Passport Center renewed it in 5 business days. The UPS Store in Pahrump made overnight shipping easy.",
      service: "Expedited Passport Renewal",
    },
    {
      name: "Rick M.",
      location: "Pahrump, NV",
      rating: 5,
      test: "My son joined the military and got orders to Germany — we needed passports fast to attend his deployment ceremony. Fast Passport Center got us emergency passports in 3 business days from Pahrump. The personal checklist and document review were perfect.",
      service: "Emergency Passport Processing",
    },
    {
      name: "Kathy D.",
      location: "Amargosa Valley, NV",
      rating: 5,
      test: "Living near Death Valley, nothing is close. Needed a child passport for our daughter's school exchange program to Japan. Fast Passport Center guided us through the Pahrump acceptance office and expedited from there. Passport arrived in 7 business days. Incredible service for rural Nevada.",
      service: "Child Passport Expedited",
    },
  ],
  facilities: [
    {
      id: "pahrump-post-main",
      name: "Pahrump Main Post Office",
      type: "USPS Passport Acceptance",
      address: "2300 E Postal Dr",
      city: "Pahrump",
      state: "NV",
      zip: "89048",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Central Pahrump — primary passport acceptance for Nye County",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Primary passport acceptance facility for Pahrump and Nye County residents. Appointment required. Availability can be limited — this is the only acceptance facility within 60+ miles of Pahrump.",
      icon: "ri-mail-line",
    },
    {
      id: "nye-county-clerk",
      name: "Nye County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "101 Radar Rd",
      city: "Tonopah",
      state: "NV",
      zip: "89049",
      phone: "(775) 482-8127",
      hours: "Mon–Fri 8am–5pm",
      distance: "Tonopah — Nye County seat, approximately 160 miles north of Pahrump via US-95",
      website: "https://www.nyecountynv.gov/152/Clerk",
      websiteLabel: "Nye County Clerk",
      notes: "Nye County Clerk in Tonopah accepts applications but is 2.5+ hours from Pahrump. Pahrump Post Office or Las Vegas facilities are far more practical for Pahrump residents.",
      icon: "ri-government-line",
    },
    {
      id: "las-vegas-post-main",
      name: "Las Vegas Main Post Office",
      type: "USPS Passport Acceptance",
      address: "1001 E Sunset Rd",
      city: "Las Vegas",
      state: "NV",
      zip: "89199",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Las Vegas — about 60 miles east of Pahrump via NV-160 and Blue Diamond Rd",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Largest acceptance facility in southern Nevada. Excellent alternative for Pahrump residents — far more appointment slots and conveniently located on the southwest side of Las Vegas.",
      icon: "ri-mail-line",
    },
    {
      id: "henderson-post",
      name: "Henderson Post Office",
      type: "USPS Passport Acceptance",
      address: "404 S Boulder Hwy",
      city: "Henderson",
      state: "NV",
      zip: "89015",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm",
      distance: "Henderson — about 75 miles east of Pahrump via NV-160 and I-215",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Alternative acceptance facility in Henderson. Good availability compared to smaller rural offices.",
      icon: "ri-mail-line",
    },
    {
      id: "beatty-post",
      name: "Beatty Post Office",
      type: "USPS Passport Acceptance",
      address: "600 E Main St",
      city: "Beatty",
      state: "NV",
      zip: "89003",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–1pm",
      distance: "Beatty — about 85 miles northwest of Pahrump via US-95",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Very small rural facility serving the Amargosa Valley and Death Valley gateway area. Extremely limited hours — Pahrump or Las Vegas strongly preferred.",
      icon: "ri-mail-line",
    },
    {
      id: "pahrump-ups-store",
      name: "The UPS Store — Pahrump",
      type: "Shipping & Document Services",
      address: "150 S Highway 160, Suite B2",
      city: "Pahrump",
      state: "NV",
      zip: "89048",
      phone: "(775) 727-1800",
      hours: "Mon–Fri 8:30am–6pm, Sat 10am–3pm",
      distance: "Central Pahrump — most convenient overnight shipping location for Pahrump residents",
      website: "https://locations.theupsstore.com/nv/pahrump/150-s-hwy-160",
      websiteLabel: "Schedule & Services",
      notes: "The UPS Store in Pahrump handles overnight shipping for document submission. Does not accept passport applications but is essential for the expedited process.",
      icon: "ri-truck-line",
    },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport from Pahrump, Nevada?",
      a: "Yes. Fast Passport Center handles emergency passport processing for Pahrump residents as fast as 24 hours. The Los Angeles Passport Agency is about 280 miles (4.5 hours) from Pahrump — we serve you entirely remotely via overnight shipping from The UPS Store in Pahrump or Las Vegas. Start online, we handle the rest.",
    },
    {
      q: "How fast can I get an expedited passport from Pahrump, NV?",
      a: "Expedited passport processing from Pahrump takes 2–9 business days through Fast Passport Center. Most adult renewals don't require an in-person acceptance visit — ship overnight from The UPS Store in Pahrump or Las Vegas FedEx Office.",
    },
    {
      q: "Where is the passport acceptance office in Pahrump?",
      a: "Pahrump's primary passport acceptance location is the Pahrump Main Post Office (2300 E Postal Dr). For better appointment availability, the Las Vegas Main Post Office (1001 E Sunset Rd, about 60 miles east) has far more slots and is conveniently located on the southwest side of Las Vegas.",
    },
    {
      q: "Is Pahrump a good base for Death Valley international visitors needing passport help?",
      a: "Yes — Pahrump serves as a gateway community for Death Valley National Park. International visitors and seasonal workers in the Death Valley/Amargosa Valley region can use Pahrump for passport services. Fast Passport Center handles expedited processing for travelers passing through the area.",
    },
    {
      q: "How far is Pahrump from the Los Angeles Passport Agency?",
      a: "Pahrump is approximately 280 miles from the Los Angeles Passport Agency — about 4.5 hours via NV-160 and I-15 through the Mojave Desert. For most urgent travel needs, Fast Passport Center serves Pahrump entirely remotely.",
    },
    {
      q: "What airport do Pahrump residents use for international travel?",
      a: "Harry Reid International Airport (LAS) in Las Vegas is the primary international hub for Pahrump travelers — about 60 miles east (about 1 hour) via NV-160. LAS offers direct international flights to Mexico, Canada, Europe, Asia, and Central America.",
    },
    {
      q: "Does Pahrump's large retirement community generate passport needs?",
      a: "Yes — Pahrump has a significant retirement and snowbird population. Many residents travel internationally for cruises, escorted tours to Europe, and winter stays in Mexico. Passport renewals are a regular need for Pahrump's active senior community.",
    },
    {
      q: "Can I renew my passport from Pahrump without visiting an office?",
      a: "Yes. Most adult Pahrump residents renewing their passport can do so entirely by mail — no in-person acceptance visit required for renewals. Ship overnight from The UPS Store Pahrump or Las Vegas FedEx.",
    },
    {
      q: "Can I get a child passport from Pahrump?",
      a: "Yes. Child passports require an in-person acceptance visit — Pahrump families typically use the Pahrump Post Office or Las Vegas Main Post Office. Both parents must typically be present.",
    },
    {
      q: "How much does expedited passport processing cost from Pahrump?",
      a: "Fast Passport Center pricing includes the U.S. government passport fees plus our courier service fee — which is typically 30–100% lower than FedEx Office or retail passport services.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Pahrump", address: "150 S Highway 160, Pahrump, NV 89048", hours: "Daily 8 AM – 10 PM" },
    { name: "The UPS Store — Pahrump", address: "150 S Highway 160, Suite B2, Pahrump, NV 89048", hours: "Mon–Fri 8:30 AM – 6 PM, Sat 10 AM – 3 PM" },
    { name: "Walmart Supercenter — Pahrump", address: "301 S Highway 160, Pahrump, NV 89048", hours: "Daily 6 AM – 11 PM" },
    { name: "FedEx Office — Las Vegas (Southwest)", address: "7380 S Las Vegas Blvd, Las Vegas, NV 89123", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 9 AM – 6 PM" },
    { name: "Walgreens — Las Vegas (Blue Diamond)", address: "4870 Blue Diamond Rd, Las Vegas, NV 89139", hours: "Daily 8 AM – 10 PM" },
    { name: "CVS Pharmacy — Las Vegas (Southwest)", address: "multiple locations on S Rainbow Blvd and S Durango Dr", hours: "Most open until 9 PM or later" },
    { name: "Costco Photo Center — Las Vegas (Summerlin)", address: "801 S Pavilion Center Dr, Las Vegas, NV 89144", hours: "Mon–Fri 10 AM – 8:30 PM" },
    { name: "Pahrump Main Post Office", address: "2300 E Postal Dr, Pahrump, NV 89048", hours: "Mon–Fri 9 AM – 5 PM" },
  ],
  dmvLocations: [
    {
      name: "Pahrump DMV (Satellite Office)",
      address: "1780 E Basin Ave, Pahrump, NV 89060",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Pahrump DMV satellite office — limited services compared to full DMV. REAL ID, driver licenses, and state ID cards available.",
      featured: true,
    },
    {
      name: "Las Vegas DMV — Flamingo",
      address: "8250 W Flamingo Rd, Las Vegas, NV 89147",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Full-service Las Vegas DMV — about 60 miles from Pahrump via NV-160. Most comprehensive DMV services in southern Nevada.",
      featured: false,
    },
    {
      name: "Las Vegas DMV — Sahara",
      address: "2701 E Sahara Ave, Las Vegas, NV 89104",
      hours: "Mon–Fri 8:00 AM – 5:00 PM",
      note: "Additional Las Vegas DMV full-service location. About 65 miles from Pahrump.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LAS",
      name: "Harry Reid International Airport (Las Vegas)",
      distance: "Primary international hub — about 60 miles east via NV-160 (approximately 1 hour)",
      address: "5757 Wayne Newton Blvd, Las Vegas, NV 89119",
      note: "Harry Reid International Airport is the primary international departure hub for Pahrump travelers. LAS offers direct international flights to Mexico, Canada, UK, Europe, Asia, and Central America — the most connected international airport in the region.",
      flightUrl: "https://www.harryreidairport.com/",
      flightLabel: "LAS Airport Website",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "About 280 miles southwest via NV-160 and I-15 (approximately 4.5 hours)",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub with more flight options than LAS — especially for Asia-Pacific and South America routes. Long drive but viable for Pahrump travelers.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Airport Website",
      featured: false,
    },
    {
      code: "ONT",
      name: "Ontario International Airport (California)",
      distance: "About 230 miles southwest via I-15 (approximately 3.5 hours)",
      address: "2500 E Airport Dr, Ontario, CA 91761",
      note: "Alternative California airport — closer than LAX for Pahrump travelers. Limited international service but offers connections to Mexico and Central America.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "ONT Airport Website",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport — Pahrump & Nye County",
      description: "Mexico vacation departing soon? Family emergency overseas? Fast Passport Center handles emergency passport processing for Pahrump residents — as fast as 24 hours. Serve entirely remotely, no desert drive to Los Angeles required.",
      bullets: ["As fast as 24 hours processing", "Reserved Los Angeles agency submission", "Dedicated agent and live status tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Expedited Passport Renewal",
      description: "Passport expired and you're 60 miles from Las Vegas and 280 from LA? Pahrump residents renew fast through Fast Passport Center — ship overnight from The UPS Store Pahrump or Las Vegas FedEx.",
      bullets: ["Fastest renewal for Nye County residents", "Ship overnight from Pahrump or Las Vegas", "Professional document review included"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost Passport Replacement",
      description: "Lost your passport in Pahrump or before an international trip? We guide Nye County travelers through DS-64 and expedited replacement.",
      bullets: ["DS-64 theft/loss reporting guidance", "Expedited replacement timelines available", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport — Pahrump Families",
      description: "Pahrump families heading to Mexico, Canada, or beyond need child passports. We guide you through the Pahrump Post Office or Las Vegas acceptance step.",
      bullets: ["Both parents typically required to consent", "Pahrump Post Office or Las Vegas acceptance guidance", "Expedited after in-person acceptance step"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-add-line",
      title: "First-Time Passport Applications",
      description: "First passport from Pahrump? In-person acceptance at the Pahrump Post Office or Las Vegas facilities is required for first-timers. We guide you through every step.",
      bullets: ["Step-by-step acceptance guidance for Pahrump and Las Vegas", "Photo location guide for Pahrump and southern Nevada", "Expedited processing after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-truck-line",
      title: "Registered State Dept. Courier Service",
      description: "Fast Passport Center is a registered U.S. Department of State courier — not a reseller. We submit through reserved agency channels at 30–100% lower prices than retail services.",
      bullets: ["State Dept. registered courier", "Best price guarantee", "Not affected by government shutdowns"],
      badge: "Registered Courier",
      link: "https://fastpassportcenter.com/checkout/",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change & Specialty Passport Services",
      description: "Name change passports, damaged passport replacement, second passports for frequent LAS international travelers, and Pahrump residents with complex passport needs.",
      bullets: ["Name change after marriage", "Damaged passport replacement", "Second passport for frequent travelers from LAS"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Same-day emergency passport processing for Pahrump, Nevada residents with urgent international travel needs." },
    { name: "Expedited Passport", description: "Expedited passport processing for Pahrump and Nye County residents in 2–9 business days." },
    { name: "Passport Renewal", description: "Fast passport renewal for Pahrump, Nevada — ship overnight from The UPS Store Pahrump or Las Vegas FedEx." },
    { name: "Lost Passport Replacement", description: "Emergency passport replacement for Pahrump travelers with lost or stolen passports." },
    { name: "Child Passport", description: "Expedited child passport services for Pahrump and Nye County families." },
    { name: "Passport Courier Service", description: "Registered U.S. Department of State courier serving Pahrump, Nevada and Nye County." },
  ],
  mistakes: [
    { icon: "ri-time-line", title: "Booking Mexico Vacations Through LAS Without Checking Passport Validity", description: "Pahrump residents book Mexico resort packages through LAS — flights are cheap and frequent — but many discover expired passports 2–3 weeks before departure." },
    { icon: "ri-camera-off-line", title: "Submitting Poor-Quality Passport Photos", description: "Photos from Pahrump's limited retail options or self-taken photos frequently get rejected. Fast Passport Center reviews photo specifications before submission." },
    { icon: "ri-file-damage-line", title: "Incomplete Forms or Missing Signatures", description: "One missing signature returns your application — costing weeks. Our pre-submission review catches all errors." },
    { icon: "ri-pen-nib-line", title: "Wrong Birth Certificate Format", description: "Short-form birth certificates are rejected. We specify exactly what document format is needed based on your state of birth." },
    { icon: "ri-map-pin-time-line", title: "Relying Only on the Pahrump Post Office for Acceptance", description: "The Pahrump Post Office has very limited appointment availability. Las Vegas Main Post Office (60 miles) has far more slots — plan ahead and consider both options." },
    { icon: "ri-file-damage-line", title: "Snowbird Passport Expiration During Extended Mexico Stays", description: "Pahrump's snowbird community often stays in Mexico for 3–6 months. A passport that's valid for departure may expire during the stay — check validity through your planned return date." },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose your processing speed based on your Pahrump travel date. Complete your secure order from home in minutes." },
    { number: "02", icon: "ri-file-list-3-line", title: "Receive Your Personalized Checklist", description: "We send a personalized document checklist — with Pahrump and Las Vegas acceptance location guidance." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Documents Overnight", description: "Drop off at The UPS Store in Pahrump or Las Vegas FedEx Office — both handle overnight delivery for expedited processing." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Agency Access", description: "We submit through reserved Los Angeles Passport Agency slots — bypassing the public appointment backlog." },
    { number: "05", icon: "ri-passport-line", title: "Passport Shipped to Your Door", description: "Your completed passport ships directly to your Pahrump or Nye County address. Live status tracking included." },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Must drive to Los Angeles — 4.5 hours from Pahrump",
        "Life-or-death emergencies only",
        "Very limited appointment availability",
        "Desert drive on I-15 through Mojave",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Ship overnight from Pahrump or Las Vegas",
        "Document review included",
        "Dedicated agent on your case",
        "Daily status updates",
        "Best price guarantee",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than USPS self-service",
        "Document review included",
        "Dedicated agent",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "Standard mail from rural Nevada",
        "6–13 weeks; can be longer from Pahrump",
        "In-person clerk appointment at Pahrump Post Office",
        "No document review — errors cause rejection",
        "No dedicated agent",
        "Not suitable if travel is under 10 weeks away",
      ],
      highlight: false,
      muted: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — weeks-long wait, Los Angeles only (280 miles)", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Drive to Los Angeles — 4.5 hours from Pahrump via I-15", fpc: "Ship overnight from Pahrump or Las Vegas" },
    { feature: "Processing Speed", agency: "Strict eligibility requirements", fpc: "As fast as 24 hours — multiple tiers" },
    { feature: "Document Review", agency: "None — errors cause rejection", fpc: "Professional review included" },
    { feature: "Rural Nevada Accessibility", agency: "Not designed for rural community residents", fpc: "Fully remote service via overnight shipping" },
    { feature: "Government Shutdowns", agency: "May be impacted", fpc: "Not affected" },
    { feature: "Best Price Guarantee", agency: "Govt. fees + 9-hour LA round trip through desert", fpc: "Best price guarantee — no travel required" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Acceptance Locations", href: "#acceptance-facilities" },
    { label: "vs. Regional Agency", href: "#comparison" },
    { label: "FAQ", href: "#faq" },
    { label: "Local Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Las Vegas", route: "/cities/las-vegas" },
    { name: "Emergency passport help in Henderson", route: "/cities/henderson-nv" },
    { name: "Fast passport renewal for Nevada residents", route: "/states/nevada" },
    { name: "Rush passport services in Nye County", route: "/states/nevada" },
    { name: "Expedited passports statewide in Nevada", route: "/states/nevada" },
  ],
  fpcOfficeNote: "After placing your order online, we send your personalized checklist and overnight shipping instructions. Ship from The UPS Store in Pahrump or a Las Vegas FedEx Office — both available weekday evenings. No visit to our processing facility required.",
  fpcOfficeDistance: "Serving Pahrump, Nye County, and the Death Valley gateway region remotely",
  serviceAreaIntro: "Whether you are in Pahrump, Beatty, Amargosa Valley, Crystal, or anywhere in Nye County — Fast Passport Center makes emergency and expedited passport processing simple without the desert drive to Los Angeles.",
  nearbyCitiesIntro: "We serve all Pahrump and Nye County zip codes including Beatty, Amargosa Valley, Crystal, Sandy Valley, and the southern Nye County region.",
  howItWorksIntro: "Getting an emergency or expedited passport from Pahrump is simpler than a 4.5-hour drive through the Mojave Desert to Los Angeles. Most Nye County clients never visit an office.",
  mistakesIntro: "These are the most common mistakes that cause delays for Pahrump and Nye County passport applicants. We review your documents before submission to prevent all of them.",
  testimonialsIntro: "Real reviews from Pahrump, Amargosa Valley, and Nye County travelers who needed their passports fast.",
  faqIntro: "The most common questions Pahrump and Nye County residents ask about emergency and expedited passport services.",
  statePageRoute: "/states/nevada",
  statePageName: "Nevada",
  travelTiming: {
    title: "Travel & Passport Timing for Pahrump and Nye County Residents",
    intro: "Pahrump is one of the largest unincorporated communities in the United States — a sprawling desert town at the edge of Nye County, 60 miles from Las Vegas and 50 miles from Death Valley National Park. Its unique position — close enough to Las Vegas for convenience but far enough to feel remote, with a large retirement and snowbird population — creates distinctive international travel patterns.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "Las Vegas Gateway — the 60-Mile Airport Connection",
        subtitle: "Harry Reid International is Pahrump's passport to the world",
        description: "Pahrump's proximity to Harry Reid International Airport (LAS) — just 60 miles away — makes international travel surprisingly accessible for a rural Nevada community. LAS offers direct flights to Mexico City, Cancun, Puerto Vallarta, London, Amsterdam, Seoul, Tokyo, Vancouver, and Toronto. Pahrump residents can be at an international departure gate in under 90 minutes from their doorstep. This accessibility creates a steady flow of last-minute Mexico bookings and international vacation planning.",
        tip: "Pahrump residents should treat LAS as their home airport for international travel. Check passport validity when booking any flight through LAS — the airport's extensive international network means passport issues surface frequently.",
      },
      {
        icon: "ri-sun-line",
        title: "Snowbird Winter Travel — Mexico & Beyond",
        subtitle: "Pahrump's large retiree population drives seasonal international travel",
        description: "Pahrump has a significant retirement and snowbird community. Many residents spend winters in Mexico (San Carlos, Puerto Vallarta, Mazatlan, Rosarito), Costa Rica, and Panama — typically November through April. Retirees booking multi-month stays need to ensure passport validity extends through their entire planned stay, not just departure date. Passport renewals peak in September–October as snowbirds prepare for winter migration.",
        tip: "Snowbirds staying 3–6 months abroad: check that your passport is valid for your full stay plus 3–6 months beyond. Some countries require 6 months validity beyond your intended departure date. Don't let a winter in Mexico get ruined by an expiring passport.",
      },
      {
        icon: "ri-global-line",
        title: "Death Valley International Tourism Connection",
        subtitle: "Pahrump serves as a gateway for international visitors to Death Valley",
        description: "Pahrump's location near Death Valley National Park means the community regularly interacts with international tourists. European visitors (especially German, French, and British) often pass through Pahrump en route to Death Valley. Some Pahrump residents work in Death Valley tourism or meet international visitors who become friends — creating unexpected international travel opportunities and passport needs.",
        tip: "If you work in or near Death Valley tourism, keep a valid passport. International connections made through park visits can lead to travel opportunities with short notice.",
      },
      {
        icon: "ri-car-line",
        title: "Mexico Border — Land Crossings & Baja Trips",
        subtitle: "Baja California is accessible from Pahrump via I-15",
        description: "Pahrump's I-15 access makes the Mexico border reachable — about 5 hours to the San Ysidro crossing at Tijuana. Baja California road trips (Ensenada, Rosarito, Valle de Guadalupe wine country) are popular with Pahrump residents. While land crossings to Mexico technically allow other documents, returning to the U.S. requires a passport, passport card, or enhanced driver's license.",
        tip: "For Baja California road trips through the San Ysidro crossing, a passport card is the most convenient document — easier to carry than a passport book and accepted at land border crossings.",
      },
    ],
    callout: {
      icon: "ri-landscape-line",
      title: "Pahrump, Nevada — Desert Gateway Between Vegas and Death Valley",
      description: "Pahrump sits at the crossroads of rural Nevada living and Las Vegas accessibility. Its residents — from retirees and snowbirds to Death Valley tourism workers to Las Vegas commuters — create a diverse international travel profile. Fast Passport Center has served Pahrump and Nye County residents with expedited passports for Mexico vacations, snowbird winter travel, and last-minute LAS international departures.",
    },
  },
  destinations: {
    title: "Where Pahrump Travelers Go — And Why Passports Get Urgent",
    intro: "These are the destinations and travel patterns we see most from Pahrump and Nye County clients. LAS gateway accessibility, snowbird migration patterns, and the rural Nevada lifestyle create distinctive passport urgency situations.",
    stats: [
      { stat: "60 mi", label: "to Harry Reid International Airport (LAS) — primary international gateway for Pahrump" },
      { stat: "280 mi", label: "to Los Angeles Passport Agency — the closest regional passport office" },
      { stat: "1 hr", label: "to LAS international departures — among the best airport access from any rural community" },
      { stat: "2–9", label: "business days for Fast Passport Center expedited processing from Pahrump" },
    ],
    destinations: [
      { name: "Mexico — Puerto Vallarta, Cancun, Cabo, Mazatlan", context: "The #1 international destination — cheap direct flights from LAS, all-inclusive resorts, and snowbird winter stays", urgency: "Booked 2–6 weeks before departure; LAS flight deals drive spontaneous bookings", icon: "ri-sun-cloudy-line" },
      { name: "Costa Rica & Panama — Snowbird & Eco-Travel", context: "Winter escapes for Pahrump's active retiree community seeking tropical eco-adventures", urgency: "October–November booking for December–March stays; 3–5 weeks before departure", icon: "ri-leaf-line" },
      { name: "Canada — Vancouver, Toronto, Calgary", context: "Direct flights from LAS make Canada trips accessible for Pahrump residents", urgency: "Booked 4–8 weeks out; passport required for air travel to Canada", icon: "ri-map-pin-2-line" },
      { name: "Europe — London, Paris, Rome", context: "European escorted tours popular with Pahrump's retiree community; direct LAS-Europe flights available", urgency: "Booked 3–6 months ahead; passport renewal discovered 4–8 weeks before departure", icon: "ri-global-line" },
      { name: "Baja California — Ensenada, Valle de Guadalupe", context: "Wine country and beach road trips from Pahrump via I-15 to Tijuana/San Ysidro crossing", urgency: "Weekend and short-trip bookings; passport card sufficient for land crossings", icon: "ri-car-line" },
      { name: "Asia — Tokyo, Seoul, Bangkok", context: "LAS-Asia direct flights make transpacific travel feasible for Pahrump adventure travelers", urgency: "Booked 2–4 months ahead; passport renewal needed 6–8 weeks before long-haul flights", icon: "ri-ship-line" },
    ],
    crossLinksTitle: "Explore Nearby Nevada Service Areas",
    crossLinksDesc: "We serve travelers throughout southern Nevada and the Mojave Desert region with the same expedited passport expertise.",
    crossLinks: [
      { name: "Las Vegas", route: "/cities/las-vegas" },
      { name: "Henderson", route: "/cities/henderson-nv" },
      { name: "Reno", route: "/cities/reno" },
    ],
  },
};